import { useCallback, useState } from "react"
import { Button, Stack, Typography, Visibility } from "@octopus-energy/coral/atoms"
import { FormikTextField } from "@octopus-energy/coral/formik"
import { IconEye, IconEyeSlash } from "@octopus-energy/icons"
import { useTranslations } from "next-intl"
import { Gauge } from "@/components/atoms/Gauge"
import { PASSWORD_MAX_SCORE, PASSWORD_MIN_SCORE } from "@/constants/password"
import { getPasswordStrength } from "@/utils/get-password-strength"

type PasswordFieldProps = {
    name?: string
    label?: string
    newPassword?: boolean
    disabled?: boolean
}

export function PasswordField({ label, name, newPassword, disabled }: PasswordFieldProps) {
    const t = useTranslations("PasswordField")
    const [passwordStrengthRatio, setPasswordStrengthRatio] = useState(0)
    const [isPasswordShown, setIsPasswordShown] = useState(false)

    const handlePassword = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            if (newPassword) {
                const password = event.target.value
                const strength = await getPasswordStrength(password)
                const strengthRatio = strength / PASSWORD_MAX_SCORE
                setPasswordStrengthRatio(strengthRatio)
            }
        },
        [newPassword]
    )

    const IconPassword = isPasswordShown ? IconEyeSlash : IconEye

    return (
        <Stack
            flexDirection="column"
            gap="xs"
        >
            <FormikTextField
                type={isPasswordShown ? "text" : "password"}
                name={name ?? (newPassword ? "newPassword" : "password")}
                label={label ?? (newPassword ? t("newPassword") : t("password"))}
                attributes={{ "data-testid": "password-field" }}
                endIcon={
                    <Button
                        variant="text"
                        aria-label={isPasswordShown ? t("hidePassword") : t("revealPassword")}
                        attributes={{ "data-testid": "password-visibility-button" }}
                        onClick={() => setIsPasswordShown(!isPasswordShown)}
                    >
                        <Stack alignItems="center">
                            <IconPassword size={24} />
                        </Stack>
                        <Visibility hidden>
                            {isPasswordShown ? t("hidePassword") : t("revealPassword")}
                        </Visibility>
                    </Button>
                }
                onChange={handlePassword}
                disabled={disabled}
            />
            {newPassword && (
                <Stack
                    flexDirection="column"
                    gap="xs"
                >
                    <Typography
                        textStyle="body2"
                        color="tertiary500"
                    >
                        {t("passwordStrength")}
                    </Typography>
                    <Gauge
                        ratio={passwordStrengthRatio}
                        acceptanceRatio={PASSWORD_MIN_SCORE / PASSWORD_MAX_SCORE}
                    />
                </Stack>
            )}
        </Stack>
    )
}

PasswordField.messages = ["PasswordField"] as const
